import type {
  FulfillmentStatus,
  FulfillmentType,
  OrderStatus,
  PaymentMethod,
  PaymentStatus,
  PickupStatus,
} from '@/lib/api-admin/types';
import type { OrderMall } from './orderMall';
import type { OrderLabelCode } from './displayLabelFilter';
import { orderKeywordQuery, type OrderSearchType } from './orderSearchType';

/**
 * Canonical order-list filter values shared by the list query (ID64) and the
 * export dialog (ID63). Multi-select axes are arrays serialized as repeated
 * **plural** query params (`orderStatuses=A&orderStatuses=B`) per BE ID64.
 */
export interface OrderFilterValues {
  searchType?: OrderSearchType;
  q?: string;
  ordererName?: string;
  ordererPhone?: string;
  malls?: ReadonlyArray<OrderMall>;
  paymentMethods?: ReadonlyArray<PaymentMethod>;
  displayLabelCodes?: ReadonlyArray<OrderLabelCode>;
  orderStatuses?: ReadonlyArray<OrderStatus>;
  paymentStatuses?: ReadonlyArray<PaymentStatus>;
  fulfillmentTypes?: ReadonlyArray<FulfillmentType>;
  fulfillmentStatuses?: ReadonlyArray<FulfillmentStatus>;
  pickupStatuses?: ReadonlyArray<PickupStatus>;
  dateFrom?: string | null;
  dateTo?: string | null;
  amountMin?: number | null;
  amountMax?: number | null;
}

const isBlank = (v: string | null | undefined): boolean => v == null || v.length === 0;

/** 이행 유형은 BE 가 단일축 — 하나만 골랐을 때만 보낸다(둘 다 = 전체). */
export const singleFulfillmentType = (
  types: ReadonlyArray<FulfillmentType> | undefined,
): FulfillmentType | undefined => (types && types.length === 1 ? types[0] : undefined);

/**
 * Append a list filter as repeated query params onto a URLSearchParams.
 * Used by the export dialog which builds a raw URL (export endpoint is not in
 * the generated OpenAPI surface). Empty arrays / blanks are skipped.
 */
export const appendOrderFilterParams = (
  qs: URLSearchParams,
  f: OrderFilterValues,
): URLSearchParams => {
  // QA-611 검색어 유형 — 목록과 같은 조건(주문번호 = orderNos 완전일치, 그 외 searchType + keyword).
  const kw = orderKeywordQuery(f.searchType, f.q);
  (kw.orderNos ?? []).forEach((v) => qs.append('orderNos', v));
  if (kw.searchType) qs.set('searchType', kw.searchType);
  if (kw.keyword) qs.set('keyword', kw.keyword);
  if (kw.q) qs.set('q', kw.q);
  if (!isBlank(f.ordererName)) qs.set('ordererName', f.ordererName as string);
  if (!isBlank(f.ordererPhone)) qs.set('ordererPhone', f.ordererPhone as string);
  const fulfillmentType = singleFulfillmentType(f.fulfillmentTypes);
  if (fulfillmentType) qs.set('fulfillmentType', fulfillmentType);
  if (!isBlank(f.dateFrom)) qs.set('dateFrom', f.dateFrom as string);
  if (!isBlank(f.dateTo)) qs.set('dateTo', f.dateTo as string);
  if (f.amountMin != null) qs.set('amountMin', String(f.amountMin));
  if (f.amountMax != null) qs.set('amountMax', String(f.amountMax));

  (f.malls ?? []).forEach((v) => qs.append('malls', v));
  (f.displayLabelCodes ?? []).forEach((v) => qs.append('displayLabelCodes', v));
  (f.orderStatuses ?? []).forEach((v) => qs.append('orderStatuses', v));
  (f.paymentStatuses ?? []).forEach((v) => qs.append('paymentStatuses', v));
  (f.fulfillmentStatuses ?? []).forEach((v) => qs.append('fulfillmentStatuses', v));
  (f.pickupStatuses ?? []).forEach((v) => qs.append('pickupStatuses', v));
  (f.paymentMethods ?? []).forEach((v) => qs.append('paymentMethods', v));

  return qs;
};
